import React, { useState, useEffect, useCallback, useRef } from "react";
import { usePermissions } from "./PermissionsContext";

const API_BASE = "https://api.seritonex.com";

const getOrgId = () => {
  const raw = sessionStorage.getItem("organizationId");
  const parsed = raw ? Number(raw) : null;
  return parsed && !isNaN(parsed) ? parsed : null;
};

const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function apiFetch(method, path, body = null) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: authHeaders(),
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) {
    return null;
  }
}

const API = {
  // Asset Types
  createAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/create-type", data),
  updateAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/update-type", data),
  deleteAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/delete-type", data),
  fetchAllAssetTypes: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/types", data),
  fetchProperties: () => apiFetch("POST", "/api/v1/amis/asset/properties", {}),

  // Assets
  createAsset: (data) => apiFetch("POST", "/api/v1/amis/asset/create", data),
  updateAsset: (data) => apiFetch("POST", "/api/v1/amis/asset/update", data),
  deleteAsset: (data) => apiFetch("POST", "/api/v1/amis/asset/delete", data),
  fetchAssetList: (data) => apiFetch("POST", "/api/v1/amis/asset/list", data),
  fetchAssetDetails: (data) => apiFetch("POST", "/api/v1/amis/asset/get", data),
  fetchByBranch: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/by-branch", data),
  fetchByWarehouse: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/by-warehouse", data),
  fetchByAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/by-type", data),
  // Branches & Warehouses
  getBranchesByUser: (user_id) =>
    apiFetch("POST", "/api/v1/amis/branch/by-user-id", { user_id }),
  getWarehousesByBranch: (branch_id) =>
    apiFetch("POST", "/api/v1/amis/warehouse/get-by-branch-id", { branch_id }),
  getBranches: () => apiFetch("GET", "/api/v1/amis/branch/get-all"),
};

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toasts, remove }) {
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span style={{ fontSize: 16 }}>
            {t.type === "success"
              ? "✓"
              : t.type === "danger"
                ? "✕"
                : t.type === "warning"
                  ? "⚠"
                  : "ℹ"}
          </span>
          <span style={{ flex: 1 }}>{t.msg}</span>
          <button
            onClick={() => remove(t.id)}
            style={{
              background: "none",
              border: "none",
              color: "var(--text3)",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            <i class="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "info") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4000);
  }, []);
  const remove = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  return { toasts, show, remove };
}

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, msg, onConfirm, onCancel, danger }) {
  return (
    <div className="overlay" onClick={onCancel}>
      <div
        className="modal"
        style={{ maxWidth: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhdr">
          <span style={{ fontSize: 20 }}>{danger ? "" : ""}</span>
          <span className="mtitle">{title}</span>
          <button className="btn btn-g btn-sm btn-ico" onClick={onCancel}>
            <i class="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>
            {msg}
          </p>
        </div>
        <div className="mfoot">
          <button className="btn btn-s" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`btn ${danger ? "btn-d" : "btn-p"}`}
            onClick={onConfirm}
          >
            {danger ? "Delete" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Asset Type Modal ──────────────────────────────────────────────────────────
function AssetTypeModal({ mode, initial, properties, onClose, onSave }) {
  const [form, setForm] = useState(
    initial || {
      asset_type_id: "",
      type_code: "",
      description: "",
      organization_id: "",
      asset_type_properties: [
        {
          link_id: null,
          is_linked: null,
          prop_value: null,
          property_id: null,
          prop_description: null,
          asset_type_property_id: null,
        },
      ],
    },
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addProp = () =>
    setForm((f) => {
      const newProp = {
        property_id: "",
        prop_description: "",
        is_linked: false,
        link_id: "",
        prop_value: "",
      };

      // If NOT linked, set link_id to "0"
      if (newProp.is_linked === false) {
        newProp.link_id = "0";
      }

      return {
        ...f,
        properties: [...f.properties, newProp],
      };
    });

  const setProp = (i, k, v) =>
    setForm((f) => {
      const props = [...f.properties];
      props[i] = { ...props[i], [k]: v };
      return { ...f, properties: props };
    });

  const removeProp = (i) =>
    setForm((f) => ({
      ...f,
      properties: f.properties.filter((_, idx) => idx !== i),
    }));

  const handleSave = async () => {
    if (!form.type_code.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mhdr">
          <span style={{ fontSize: 20 }}></span>
          <span className="mtitle">
            {mode === "create" ? "Create Asset Type" : "Edit Asset Type"}
          </span>
          <button className="btn btn-g btn-sm btn-ico" onClick={onClose}>
            <i class="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <div className="frow mb12">
            <div className="fg">
              <label className="fl">Type Code *</label>
              <input
                className="fi"
                value={form.type_code}
                onChange={(e) => set("type_code", e.target.value)}
                placeholder="e.g. LAPTOP"
              />
            </div>
            <div className="fg">
              <label className="fl">Organization ID</label>
              <input
                className="fi"
                value={form.organization_id}
                onChange={(e) => set("organization_id", e.target.value)}
                placeholder="Org ID"
              />
            </div>
          </div>
          <div className="fg mb12">
            <label className="fl">Description</label>
            <input
              className="fi"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Brief description"
            />
          </div>

          <div className="fb mb8">
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text3)",
                textTransform: "uppercase",
                letterSpacing: ".1em",
              }}
            >
              Properties
            </span>
            <button className="btn btn-s btn-sm" onClick={addProp}>
              + Add Property
            </button>
          </div>

          {form.properties.length === 0 && (
            <div className="empty-branch mb12">
              <div style={{ fontSize: 28, marginBottom: 6 }}></div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>
                No properties yet. Add one above.
              </div>
            </div>
          )}

          {form.properties.map((p, i) => (
            <div key={i} className="product-line-row mb8">
              <div className="product-line-cell" style={{ flex: "2 1 140px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Property ID
                </label>
                <input
                  className="fi"
                  value={p.property_id}
                  onChange={(e) => setProp(i, "property_id", e.target.value)}
                  placeholder="ID"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "3 1 180px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Description
                </label>
                <input
                  className="fi"
                  value={p.prop_description}
                  onChange={(e) =>
                    setProp(i, "prop_description", e.target.value)
                  }
                  placeholder="Prop description"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "2 1 120px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Value
                </label>
                <input
                  className="fi"
                  value={p.prop_value}
                  onChange={(e) => setProp(i, "prop_value", e.target.value)}
                  placeholder="Value"
                />
              </div>
              <div className="product-line-cell product-line-faulty">
                <label className="fl" style={{ fontSize: 9 }}>
                  Linked
                </label>
                <button
                  type="button"
                  className={`faulty-toggle${p.is_linked ? " on" : ""}`}
                  onClick={() => setProp(i, "is_linked", !p.is_linked)}
                >
                  <div className={`faulty-pill${p.is_linked ? " on" : ""}`}>
                    <div className="faulty-knob" />
                  </div>
                  <span className="faulty-label">
                    {p.is_linked ? "Yes" : "No"}
                  </span>
                </button>
              </div>
              {p.is_linked && (
                <div
                  className="product-line-cell"
                  style={{ flex: "2 1 100px" }}
                >
                  <label className="fl" style={{ fontSize: 9 }}>
                    Link ID
                  </label>
                  <input
                    className="fi"
                    value={p.link_id}
                    onChange={(e) => setProp(i, "link_id", e.target.value)}
                    placeholder="Link ID"
                  />
                </div>
              )}
              <div className="product-line-cell product-line-remove">
                <label className="fl" style={{ fontSize: 9 }}>
                  &nbsp;
                </label>
                <button
                  className="btn btn-d btn-sm product-remove-btn"
                  onClick={() => removeProp(i)}
                >
                  Close
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mfoot">
          <button className="btn btn-s" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-p" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spin-icon" /> Saving…
              </>
            ) : mode === "create" ? (
              "Create Type"
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Asset Modal ───────────────────────────────────────────────────────────────
function AssetModal({
  mode,
  initial,
  assetTypes,
  properties,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(
    initial || {
      serial_number: "",
      asset_type_id: "",
      asset_description: "",
      organization_id: "",
      properties: [],
    },
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addProp = () =>
    setForm((f) => ({
      ...f,
      properties: [
        ...f.properties,
        { property_id: "", prop_description: "", prop_value: "" },
      ],
    }));

  const setProp = (i, k, v) =>
    setForm((f) => {
      const props = [...f.properties];
      props[i] = { ...props[i], [k]: v };
      return { ...f, properties: props };
    });

  const removeProp = (i) =>
    setForm((f) => ({
      ...f,
      properties: f.properties.filter((_, idx) => idx !== i),
    }));

  const handleSave = async () => {
    if (!form.serial_number.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mhdr">
          <span style={{ fontSize: 20 }}></span>
          <span className="mtitle">
            {mode === "create" ? "Register New Asset" : "Edit Asset"}
          </span>
          <button className="btn btn-g btn-sm btn-ico" onClick={onClose}>
            <i class="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <div className="frow mb12">
            <div className="fg">
              <label className="fl">Serial Number *</label>
              <input
                className="fi"
                value={form.serial_number}
                onChange={(e) => set("serial_number", e.target.value)}
                placeholder="e.g. SN-20240001"
              />
            </div>
            <div className="fg">
              <label className="fl">Asset Type</label>
              <select
                className="fi fsel"
                value={form.asset_type_id}
                onChange={(e) => set("asset_type_id", e.target.value)}
              >
                <option value="">— Select type —</option>
                {assetTypes.map((t) => (
                  <option
                    key={t.asset_type_code || t.code}
                    value={t.asset_type_code || t.code}
                  >
                    {t.type_code || t.description || t.asset_type_code}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="frow mb12">
            <div className="fg">
              <label className="fl">Organization ID</label>
              <input
                className="fi"
                value={form.organization_decsription}
                onChange={(e) => set("organization_id", e.target.value)}
                placeholder="Org ID"
              />
            </div>
            <div className="fg">
              <label className="fl">Description</label>
              <input
                className="fi"
                value={form.asset_description}
                onChange={(e) => set("asset_description", e.target.value)}
                placeholder="Brief description"
              />
            </div>
          </div>

          <div className="fb mb8">
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text3)",
                textTransform: "uppercase",
                letterSpacing: ".1em",
              }}
            >
              Properties
            </span>
            <button className="btn btn-s btn-sm" onClick={addProp}>
              + Add Property
            </button>
          </div>

          {form.properties.length === 0 && (
            <div className="empty-branch mb12">
              <div style={{ fontSize: 28, marginBottom: 6 }}></div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>
                No properties. Add one above.
              </div>
            </div>
          )}

          {form.properties.map((p, i) => (
            <div key={i} className="product-line-row mb8">
              <div className="product-line-cell" style={{ flex: "2 1 120px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Property ID
                </label>
                <input
                  className="fi"
                  value={p.property_id}
                  onChange={(e) => setProp(i, "property_id", e.target.value)}
                  placeholder="ID"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "3 1 160px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Description
                </label>
                <input
                  className="fi"
                  value={p.prop_description}
                  onChange={(e) =>
                    setProp(i, "prop_description", e.target.value)
                  }
                  placeholder="Label"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "3 1 160px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Value
                </label>
                <input
                  className="fi"
                  value={p.prop_value}
                  onChange={(e) => setProp(i, "prop_value", e.target.value)}
                  placeholder="Value"
                />
              </div>
              <div className="product-line-cell product-line-remove">
                <label className="fl" style={{ fontSize: 9 }}>
                  &nbsp;
                </label>
                <button
                  className="btn btn-d btn-sm product-remove-btn"
                  onClick={() => removeProp(i)}
                >
                  <i class="bi bi-x-lg" style={{ marginLeft: 1 }}></i>
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mfoot">
          <button className="btn btn-s" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-p" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spin-icon" /> Saving…
              </>
            ) : mode === "create" ? (
              "Register Asset"
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Asset Detail Modal ────────────────────────────────────────────────────────
function AssetDetailModal({ asset, onClose, onEdit, onDelete }) {
  if (!asset) return null;
  const props = asset.properties || [];
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mhdr">
          <span style={{ fontSize: 20 }}></span>
          <div style={{ flex: 1 }}>
            <div className="mtitle">
              {asset.serial_number || "Asset Details"}
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)" }}>
              ID: {asset.asset_id || asset.id || "—"}
            </div>
          </div>
          <button
            className="btn btn-g btn-sm"
            onClick={() => {
              onClose();
              onEdit(asset);
            }}
          >
            Edit
          </button>
          <button
            className="btn btn-d btn-sm"
            onClick={() => {
              onClose();
              onDelete(asset);
            }}
            style={{ marginright: 80 }}
          >
            <i class="bi bi-trash"></i>
          </button>
          <button className="btn btn-g btn-sm btn-ico" onClick={onClose}>
            <i class="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <div className="review-block mb16">
            <div className="review-row">
              <span className="review-label">Serial #</span>
              <span className="review-value">{asset.serial_number || "—"}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Type Code</span>
              <span className="review-value">
                {asset.asset_type_code || "—"}
              </span>
            </div>
            <div className="review-row">
              <span className="review-label">Description</span>
              <span className="review-value">
                {asset.asset_description || "—"}
              </span>
            </div>
            <div className="review-row">
              <span className="review-label">Org ID</span>
              <span className="review-value">
                {asset.organization_description || "—"}
              </span>
            </div>
          </div>

          {props.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text3)",
                  textTransform: "uppercase",
                  letterSpacing: ".1em",
                  marginBottom: 8,
                }}
              >
                Properties
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Property ID</th>
                      <th>Description</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.map((p, i) => (
                      <tr key={i}>
                        <td className="mono">{p.property_id}</td>
                        <td>{p.prop_description}</td>
                        <td>{p.prop_value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="mfoot">
          <button className="btn btn-s" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Filter Modal ──────────────────────────────────────────────────────────────
function FilterModal({ onClose, onApply }) {
  const [mode, setMode] = useState("org");
  const [value, setValue] = useState("");
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  useEffect(() => {
    const loadBranches = async () => {
      setLoadingBranches(true);
      const rawUser = sessionStorage.getItem("userId");
      const userId = rawUser ? Number(rawUser) : 0;

      let res = null;
      if (userId) res = await API.getBranchesByUser(userId);

      if (!res) res = await API.getBranches();

      setLoadingBranches(false);
      if (res) {
        const list = Array.isArray(res)
          ? res
          : res.branches || res.data || res.items || [];
        setBranches(Array.isArray(list) ? list : []);
      } else {
        setBranches([]);
      }
    };
    loadBranches();
  }, []);

  const loadWarehouses = async (branchId) => {
    setLoadingWarehouses(true);
    const res = await API.getWarehousesByBranch(Number(branchId));
    setLoadingWarehouses(false);
    if (res) {
      const list = Array.isArray(res) ? res : res.warehouses || res.data || [];
      setWarehouses(Array.isArray(list) ? list : []);
    }
  };

  const handleModeChange = (m) => {
    setMode(m);
    setValue("");
    if (m === "warehouse") {
      setWarehouses([]);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 520 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhdr">
          <span style={{ fontSize: 20 }}></span>
          <span className="mtitle">Filter Assets</span>
          <button className="btn btn-g btn-sm btn-ico" onClick={onClose}>
            <i class="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <div className="fg mb12">
            <label className="fl">Filter By</label>
            <select
              className="fi fsel"
              value={mode}
              onChange={(e) => handleModeChange(e.target.value)}
            >
              <option value="org">Organization</option>
              <option value="branch">Branch</option>
              <option value="warehouse">Warehouse</option>
              <option value="asset">Asset ID</option>
            </select>
          </div>

          {mode === "org" && (
            <div className="fg">
              <label className="fl">Organization ID</label>
              <input
                className="fi"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={String(getOrgId() || "Enter org id…")}
              />
            </div>
          )}

          {mode === "branch" && (
            <div className="fg">
              <label className="fl">Branch</label>
              <select
                className="fi fsel"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              >
                <option value="">— Select branch —</option>
                {loadingBranches ? (
                  <option value="">Loading…</option>
                ) : (
                  branches.map((b) => (
                    <option
                      key={b.branch_id || b.id}
                      value={b.branch_id || b.id}
                    >
                      {b.name || b.branch_name || `${b.branch_id || b.id}`}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          {mode === "warehouse" && (
            <>
              <div className="fg mb12">
                <label className="fl">Branch</label>
                <select
                  className="fi fsel"
                  onChange={(e) => {
                    const bid = e.target.value;
                    setValue("");
                    if (bid) loadWarehouses(bid);
                  }}
                >
                  <option value="">— Select branch —</option>
                  {loadingBranches ? (
                    <option value="">Loading…</option>
                  ) : (
                    branches.map((b) => (
                      <option
                        key={b.branch_id || b.id}
                        value={b.branch_id || b.id}
                      >
                        {b.name || b.branch_name || `${b.branch_id || b.id}`}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="fg">
                <label className="fl">Warehouse</label>
                <select
                  className="fi fsel"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                >
                  <option value="">— Select warehouse —</option>
                  {loadingWarehouses ? (
                    <option value="">Loading…</option>
                  ) : (
                    warehouses.map((w) => (
                      <option
                        key={w.warehouse_id || w.id}
                        value={w.warehouse_id || w.id}
                      >
                        {w.name ||
                          w.warehouse_name ||
                          `${w.warehouse_id || w.id}`}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </>
          )}

          {mode === "asset" && (
            <div className="fg">
              <label className="fl">Asset ID</label>
              <input
                className="fi"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter asset id…"
              />
            </div>
          )}
        </div>
        <div className="mfoot">
          <button className="btn btn-s" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-p"
            onClick={() => onApply(mode, value)}
            disabled={mode !== "asset" && !value}
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Assets Tab ────────────────────────────────────────────────────────────────
function AssetsTab({ toast, assetTypes, properties }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [filterLabel, setFilterLabel] = useState("All Assets");

  const loadAll = useCallback(async () => {
    setLoading(true);
    const orgId = getOrgId();
    const payload = {};
    if (orgId !== null) payload.organization_id = orgId;
    const res = await API.fetchAssetList(payload);
    setLoading(false);
    if (res) {
      setAssets(Array.isArray(res) ? res : res.assets || []);
    } else {
      toast("Failed to load assets", "danger");
    }
  }, [toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const applyFilter = async (mode, value) => {
    setModal(null);
    setLoading(true);
    let res = null;
    if (mode === "org")
      res = await API.fetchAssetList({ organization_id: Number(value) });
    else if (mode === "branch")
      res = await API.fetchByBranch({ branch_id: Number(value) });
    else if (mode === "warehouse")
      res = await API.fetchByWarehouse({ warehouse_id: Number(value) });
    else if (mode === "asset")
      res = await API.fetchAssetDetails({ asset_id: Number(value) });
    setLoading(false);
    if (res) {
      const list = Array.isArray(res) ? res : res.assets ? res.assets : [res];
      setAssets(list);
      setFilterLabel(
        `${mode.charAt(0).toUpperCase() + mode.slice(1)} ID: ${value}`,
      );
      toast(`Showing ${list.length} result(s)`, "success");
    } else {
      toast("No results found", "warning");
    }
  };

  const handleCreate = async (form) => {
    const res = await API.createAsset({
      ...form,
      asset_type_id: Number(form.asset_type_id) || 0,
      organization_id: Number(form.organization_id) || 0,
    });
    if (res) {
      toast("Asset registered", "success");
      setModal(null);
      loadAll();
    } else toast("Failed to create asset", "danger");
  };

  const handleUpdate = async (form) => {
    const res = await API.updateAsset({
      ...form,
      asset_id: form.asset_id || form.id,
      asset_type_id: Number(form.asset_type_id) || 0,
      organization_id: Number(form.organization_id) || 0,
    });
    if (res) {
      toast("Asset updated", "success");
      setModal(null);
      loadAll();
    } else toast("Failed to update asset", "danger");
  };

  const handleDelete = async (asset) => {
    const res = await API.deleteAsset({ asset_id: asset.asset_id || asset.id });
    if (res) {
      toast("Asset deleted", "success");
      setConfirm(null);
      loadAll();
    } else toast("Failed to delete asset", "danger");
  };

  const filtered = assets.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (a.serial_number || "").toLowerCase().includes(s) ||
      (a.asset_description || "").toLowerCase().includes(s) ||
      String(a.asset_id || a.id || "").includes(s)
    );
  });

  return (
    <>
      <div className="panel">
        <div className="tbar">
          <div className="tsearch">
            <span style={{ color: "var(--text3)", fontSize: 14 }}></span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assets…"
            />
          </div>
          <span className="assets-filter-label">
            <span className="badge badge-blue">{filterLabel}</span>
          </span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              className="btn btn-s btn-sm"
              onClick={() => setModal({ type: "filter" })}
            >
              Filter
            </button>
            <button className="btn btn-s btn-sm" onClick={loadAll}>
              Refresh
            </button>
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Asset ID</th>
                <th>Serial Number</th>
                <th>Type ID</th>
                <th>Description</th>
                <th>Org ID</th>
                <th>Properties</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "center",
                      padding: 32,
                      color: "var(--text3)",
                    }}
                  >
                    <span className="spin-icon" style={{ marginRight: 8 }} />
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="empty">
                      <div className="empty-ico"></div>
                      <div>EMPTY</div>
                      <button
                        className="btn btn-p btn-sm"
                        style={{ marginTop: 10 }}
                        onClick={() => setModal({ type: "create-asset" })}
                      >
                        Register First Asset
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((a) => (
                  <tr key={a.asset_id || a.id || a.serial_number}>
                    <td className="mono">{a.asset_id || a.id || "—"}</td>
                    <td>
                      <span style={{ fontWeight: 600 }}>
                        {a.serial_number || "—"}
                      </span>
                    </td>
                    <td className="mono">{a.asset_type_id || "—"}</td>
                    <td
                      style={{
                        color: "var(--text2)",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {a.asset_description || "—"}
                    </td>
                    <td className="mono">{a.organization_id || "—"}</td>
                    <td>
                      <span className="badge badge-blue">
                        {(a.properties || []).length} props
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn btn-s btn-sm"
                          onClick={() => setModal({ type: "detail", data: a })}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-s btn-sm"
                          onClick={() =>
                            setModal({ type: "edit-asset", data: a })
                          }
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-d btn-sm"
                          onClick={() =>
                            setConfirm({ action: "delete-asset", data: a })
                          }
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal?.type === "filter" && (
        <FilterModal onClose={() => setModal(null)} onApply={applyFilter} />
      )}
      {modal?.type === "create-asset" && (
        <AssetModal
          mode="create"
          assetTypes={assetTypes}
          properties={properties}
          onClose={() => setModal(null)}
          onSave={handleCreate}
        />
      )}
      {modal?.type === "edit-asset" && (
        <AssetModal
          mode="edit"
          initial={modal.data}
          assetTypes={assetTypes}
          properties={properties}
          onClose={() => setModal(null)}
          onSave={handleUpdate}
        />
      )}
      {modal?.type === "detail" && (
        <AssetDetailModal
          asset={modal.data}
          onClose={() => setModal(null)}
          onEdit={(a) => setModal({ type: "edit-asset", data: a })}
          onDelete={(a) => setConfirm({ action: "delete-asset", data: a })}
        />
      )}
      {confirm?.action === "delete-asset" && (
        <ConfirmModal
          title="Delete Asset"
          msg={`Permanently delete asset "${confirm.data.serial_number}"? This cannot be undone.`}
          danger
          onConfirm={() => handleDelete(confirm.data)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ─── Asset Types Tab ───────────────────────────────────────────────────────────
function AssetTypesTab({ toast, onTypesChange, onBack }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [orgIdInput, setOrgIdInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    // ── Use manual input if provided, otherwise fall back to session org ──
    const sessionOrgId = getOrgId();
    const filterOrgId = orgIdInput ? Number(orgIdInput) : sessionOrgId;
    const payload = {};
    if (filterOrgId !== null && !isNaN(filterOrgId))
      payload.organization_id = filterOrgId;
    const res = await API.fetchAllAssetTypes(payload);
    setLoading(false);
    if (res) {
      const raw = Array.isArray(res)
        ? res
        : res.assetTypes || res.types || res.asset_types || [];

      const list = raw.map((t) => ({
        ...t,
        properties: t.asset_type_properties || [],
      }));
      setTypes(list);
      onTypesChange(list);
    } else toast("Failed to load asset types", "danger");
  }, [toast, orgIdInput, onTypesChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (form) => {
    const res = await API.createAssetType({
      ...form,
      organization_id: form.organization_id || "",
    });
    if (res) {
      toast("Asset type created", "success");
      setModal(null);
      load();
    } else toast("Failed to create asset type", "danger");
  };

  const handleUpdate = async (form) => {
    const res = await API.updateAssetType({
      ...form,
      asset_type_id: form.asset_type_id || form.id || 0,
    });
    if (res) {
      toast("Asset type updated", "success");
      setModal(null);
      load();
    } else toast("Failed to update asset type", "danger");
  };

  const handleDelete = async (t) => {
    const res = await API.deleteAssetType({
      asset_type_id: t.asset_type_id || t.id,
    });
    if (res) {
      toast("Asset type deleted", "success");
      setConfirm(null);
      load();
    } else toast("Failed to delete asset type", "danger");
  };

  const filtered = types.filter((t) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (t.type_code || "").toLowerCase().includes(s) ||
      (t.description || "").toLowerCase().includes(s)
    );
  });

  return (
    <>
      <div className="panel">
        <div className="tbar">
          <div className="tsearch">
            <span style={{ color: "var(--text3)", fontSize: 14 }}></span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search types…"
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button className="btn btn-s btn-sm" onClick={load}>
              Filter
            </button>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="btn btn-s btn-sm" onClick={load}>
              ↺ Refresh
            </button>
            <button
              className="btn btn-p btn-sm"
              onClick={() => setModal({ type: "create" })}
            >
              + New Type
            </button>
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Type ID</th>
                <th>Type Code</th>
                <th>Description</th>
                <th>Org ID</th>
                <th>Properties</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: 32,
                      color: "var(--text3)",
                    }}
                  >
                    <span className="spin-icon" style={{ marginRight: 8 }} />
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty">
                      <div className="empty-ico"></div>
                      <div>No asset types found</div>
                      <button
                        className="btn btn-p btn-sm"
                        style={{ marginTop: 10 }}
                        onClick={() => setModal({ type: "create" })}
                      >
                        Create First Type
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((t) => (
                  <tr key={t.asset_type_id || t.id || t.type_code}>
                    <td className="mono">{t.asset_type_id || t.id || "—"}</td>
                    <td>
                      <span className="badge badge-amber">
                        {t.type_code || "—"}
                      </span>
                    </td>
                    <td style={{ color: "var(--text2)" }}>
                      {t.description || "—"}
                    </td>
                    <td className="mono">
                      {t.organization_description || "—"}
                    </td>
                    <td>
                      <span className="badge badge-blue">
                        {(t.properties || []).length} props
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn btn-s btn-sm"
                          onClick={() => setModal({ type: "edit", data: t })}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-d btn-sm"
                          onClick={() => setConfirm({ data: t })}
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal?.type === "create" && (
        <AssetTypeModal
          mode="create"
          properties={[]}
          onClose={() => setModal(null)}
          onSave={handleCreate}
        />
      )}
      {modal?.type === "edit" && (
        <AssetTypeModal
          mode="edit"
          initial={modal.data}
          properties={[]}
          onClose={() => setModal(null)}
          onSave={handleUpdate}
        />
      )}
      {confirm && (
        <ConfirmModal
          title="Delete Asset Type"
          msg={`Permanently delete type "${confirm.data.type_code}"? Assets using this type may be affected.`}
          danger
          onConfirm={() => handleDelete(confirm.data)}
          onCancel={() => setConfirm(null)}
        />
      )}
      {onBack && (
        <div className="fb" style={{ marginTop: 18 }}>
          <button className="btn btn-g btn-sm" onClick={onBack}>
            Back to Assets
          </button>
        </div>
      )}
    </>
  );
}

// ─── Properties Tab ────────────────────────────────────────────────────────────
function PropertiesTab({ toast }) {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await API.fetchProperties();
    setLoading(false);
    if (res) setProps(Array.isArray(res) ? res : res.properties || []);
    else toast("Failed to load properties", "danger");
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="panel">
      <div className="tbar">
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text2)" }}>
          Available Property Definitions
        </span>
        <div style={{ marginLeft: "auto" }}>
          <button className="btn btn-s btn-sm" onClick={load}>
            ↺ Refresh
          </button>
        </div>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Property ID</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={2}
                  style={{
                    textAlign: "center",
                    padding: 32,
                    color: "var(--text3)",
                  }}
                >
                  <span className="spin-icon" style={{ marginRight: 8 }} />
                  Loading…
                </td>
              </tr>
            )}
            {!loading && props.length === 0 && (
              <tr>
                <td colSpan={2}>
                  <div className="empty">
                    <div className="empty-ico"></div>
                    <div>No property definitions found</div>
                  </div>
                </td>
              </tr>
            )}
            {!loading &&
              props.map((p) => (
                <tr key={p.property_id}>
                  <td className="mono">{p.property_id}</td>
                  <td>{p.description || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function Assets() {
  const { toasts, show: toast, remove } = useToast();
  const [tab, setTab] = useState("assets");
  const [assetTypes, setAssetTypes] = useState([]);
  const { canSeePage, ready } = usePermissions();

  if (ready && !canSeePage("Inventory")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">You do not have permission to view inventory.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <Toast toasts={toasts} remove={remove} />

      {/* Header (title shown in top header) */}
      <div />
      {/* Tabs */}
      <div className="tabs mb22">
        <div
          className={`tab${tab === "assets" ? " on" : ""}`}
          onClick={() => setTab("assets")}
        >
          Assets
        </div>
        <div
          className={`tab${tab === "types" ? " on" : ""}`}
          onClick={() => setTab("types")}
        >
          Asset Types
        </div>
        <div
          className={`tab${tab === "properties" ? " on" : ""}`}
          onClick={() => setTab("properties")}
        >
          Properties
        </div>
      </div>

      {tab === "assets" && (
        <AssetsTab toast={toast} assetTypes={assetTypes} properties={[]} />
      )}
      {tab === "types" && (
        <AssetTypesTab
          toast={toast}
          onTypesChange={setAssetTypes}
          onBack={() => setTab("assets")}
        />
      )}
      {tab === "properties" && <PropertiesTab toast={toast} />}
    </div>
  );
}
